import { Fragment, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { ACTIVITY_TYPE_META, dayGroupLabel, formatTz, type ActivityType } from './auditConstants';

export interface ActivityRow {
  id: number;
  createdAt: string;
  user: { id: number; name: string; email: string } | null;
  type: ActivityType;
  verb: string;
  entityLabel: string | null;
  entityRoute: string | null;
  summary: string | null;
  diff: { field: string; before: string | null; after: string | null }[];
  ip: string | null;
  device: string | null;
  requestId: string | null;
  facilityName: string | null;
}

const MONO = "ui-monospace, 'Cascadia Mono', Consolas, monospace";

export default function ActivityTimeline({
  rows, loading, total, nextCursor, onLoadMore, expandedIds, onToggleExpand,
}: {
  rows: ActivityRow[];
  loading: boolean;
  total: number;
  nextCursor: number | null;
  onLoadMore: () => void;
  expandedIds: Set<number>;
  onToggleExpand: (id: number) => void;
}) {
  // Precompute per-day event counts for the "{n} events" header, over the currently loaded rows.
  const dayCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const g = dayGroupLabel(r.createdAt);
      m.set(g, (m.get(g) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  let lastGroup: string | null = null;

  return (
    <div className="audit-in" style={{ borderRadius: 14, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading...</div>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: 14 }}>
          No activity matches these filters.
        </div>
      )}

      {!loading && rows.map((row) => {
        const group = dayGroupLabel(row.createdAt);
        const showGroup = group !== lastGroup;
        lastGroup = group;
        const meta = ACTIVITY_TYPE_META[row.type];
        const expanded = expandedIds.has(row.id);

        return (
          <Fragment key={row.id}>
            {showGroup && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border-subtle)' }}>
                <span>{group}</span>
                <span style={{ fontWeight: 500, letterSpacing: 0 }}>{dayCounts.get(group) ?? 0} events</span>
              </div>
            )}
            <div
              onClick={() => onToggleExpand(row.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', cursor: 'pointer',
                background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
              }}
              onMouseEnter={(e) => { if (!expanded) e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ width: 64, flexShrink: 0, fontFamily: MONO, fontSize: 13, color: 'var(--text-secondary)' }}>
                {formatTz(row.createdAt, { hour: '2-digit', minute: '2-digit', hour12: false })}
              </div>
              <UserAvatar userId={row.user?.id ?? null} name={row.user?.name ?? null} size="timeline" isSystem={!row.user} typeBadge={row.type} />
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <strong style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.user?.name ?? 'System'}</strong>{' '}
                  <span style={{ color: 'var(--text-secondary)' }}>{row.verb}</span>{' '}
                  {row.entityLabel && <span style={{ color: '#60a5fa', fontWeight: 500 }}>{row.entityLabel}</span>}
                </div>
                {row.summary && <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.summary}</div>}
              </div>
              <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 99, fontWeight: 600, background: `${meta.color}26`, color: meta.color, whiteSpace: 'nowrap' }}>
                {meta.label}
              </span>
              <div style={{ width: 120, flexShrink: 0, textAlign: 'right', fontFamily: MONO, fontSize: 12, color: 'var(--text-muted)' }}>
                {row.ip ?? '—'}
              </div>
              <ChevronRight size={15} color="#6b7280" className="audit-timeline-chevron" style={{ transform: expanded ? 'rotate(90deg)' : 'none', flexShrink: 0 }} />
            </div>

            {expanded && (
              <div className="audit-in" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '4px 18px 16px 114px' }}>
                <div style={{ flex: '1 1 360px', borderRadius: 10, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.03)', padding: '8px 12px' }}>
                    <span>Field</span><span>Before</span><span>After</span>
                  </div>
                  {row.diff.length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)', borderTop: '1px solid var(--border-subtle)' }}>No field changes recorded.</div>
                  )}
                  {row.diff.map((d, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '8px 12px', borderTop: '1px solid var(--border-subtle)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{d.field}</span>
                      <span style={{ fontFamily: MONO, color: '#f87171', textDecoration: d.before ? 'line-through' : 'none', textDecorationColor: 'rgba(248,113,113,0.5)' }}>{d.before ?? '—'}</span>
                      <span style={{ fontFamily: MONO, color: '#34d399' }}>{d.after ?? '—'}</span>
                    </div>
                  ))}
                </div>
                <div style={{ flex: '0 1 300px', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 12, alignContent: 'start' }}>
                  <span style={{ color: 'var(--text-muted)' }}>User</span>
                  <span style={{ color: 'var(--text-primary)' }}>{row.user?.email ?? 'System'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Facility</span>
                  <span style={{ color: 'var(--text-primary)' }}>{row.facilityName ?? '—'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>IP / device</span>
                  <span style={{ fontFamily: MONO, color: 'var(--text-primary)' }}>{row.ip ?? '—'} · {row.device ?? '—'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Request ID</span>
                  <span style={{ fontFamily: MONO, color: 'var(--text-secondary)' }}>{row.requestId ?? '—'}</span>
                  {row.entityRoute && (
                    <>
                      <span style={{ color: 'var(--text-muted)' }}>Record</span>
                      <a href={row.entityRoute} style={{ color: '#60a5fa', textDecoration: 'none' }}>Open {row.entityLabel ?? 'record'} →</a>
                    </>
                  )}
                </div>
              </div>
            )}
          </Fragment>
        );
      })}

      <div style={{ padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
        <span>Showing {rows.length} of {total} events · retained 365 days</span>
        {nextCursor !== null && (
          <button className="btn btn-ghost btn-sm" onClick={onLoadMore}>Load older ↓</button>
        )}
      </div>
    </div>
  );
}
